/* eslint-disable no-prototype-builtins */
/* eslint-disable no-unused-vars */
import Axios, { AxiosError, AxiosResponse, AxiosInstance } from 'axios';
import { AUTH_SERVICE, IUserAuthSession } from '@/services/auth.service';
import {
  API_ERROR_AUTH,
  API_ERROR_DESCARGA,
  API_ERROR_GENERICO,
  API_ERROR_INVALID_DATA,
  getApiErrorDetail,
  HTTP_STATUS_CODES,
  JsonApiError,
  JsonApiRecurso,
  JsonApiResponse,
  JsonApiResponseRecurso,
  JsonApiResponseRecursos,
  PREFIX_API_ERROR
} from './json-api.types';
import { ApiModel } from './api-model';
import { isArray, isEmpty, isPlainObject, isString } from 'lodash';
import { saveAs } from 'file-saver';

export interface QueryParams {
  [key: string]: string | string[];
}

/**
 * Clase con métodos estáticos para realizar requests a la API usando la librería Axios
 *   https://github.com/axios/axios
 */
export abstract class ApiClient {
  private static axios: AxiosInstance = Axios.create();

  /**
   * Generación de headers para requerimientos autenticados.
   */
  private static async getHeadersRequestAutenticado() {
    const jwt: string = await AUTH_SERVICE.sesionActual().then((sesion: IUserAuthSession) => sesion.getIdToken().getJwtToken());
    return { 'Authorization': 'Bearer ' + jwt };
  }

  /**
   * Request http GET genérico a la API al url indicado. Retorna body del response, chequeando que tenga data.
   * El request es autenticado mediante sesión de usuario (cookies)
   * Por defecto el método retorna tipo JsonApiResponse, pero puede invocarse con tipo genérico
   */
  static async getApi(url: string, params?: QueryParams): Promise<JsonApiResponse>; // Definición default
  static async getApi<T>(url: string, params?: QueryParams): Promise<T>; // Definición con tipo de retorno genérico
  static async getApi<T>(url: string, params?: QueryParams): Promise<T> {// Implementación
    if (!url) {
      return ApiClient.sinUrlError('GET');
    }
    return this.axios.get<T>(url, { params: params, headers: await this.getHeadersRequestAutenticado() })
      .catch(error => ApiClient.handleAxiosError(error)) // Maneja error en el GET - hace reject con JsonApiError. Si es error de autenticación, desloguea.
      .then(resp => { // Si GET ok, retorna body response, salvo que haya errores.
        // console.log('GET API',resp);
        let body: any = resp.data; // Contenido de la respuesta
        if (body.hasOwnProperty('errors') || body.hasOwnProperty('error')) {
          return ApiClient.handleApiError(body.errors || body.error); // Procesa el/los JsonApiError y hace reject
        }
        else if (!body.hasOwnProperty('data')) {
          return ApiClient.handleApiError(API_ERROR_INVALID_DATA); // reject, genera JsonApiError
        }
        // Tiene data y no error: retorna body resp
        return body;
      });

  }

  /**
   * GET a la API a endpoint que retorna response con un único recurso
   *  Alias de getApi(), sabiendo que la respuesta contiene en data un objeto único
   * Chequea que data exista y que sea un objecto
   */
  static getApiRecurso(url: string, params?: QueryParams): Promise<JsonApiResponseRecurso> {
    return ApiClient.getApi<JsonApiResponseRecurso>(url, params)
      .then(resp => {
        if (!isPlainObject(resp.data)) {
          return ApiClient.handleApiError(API_ERROR_INVALID_DATA); // reject, genera JsonApiError
        }
        return resp;
      });
  }

  /**
   * GET a la API a endpoint que retorna response con una colección de recursos
   *  Alias de getApi(), sabiendo que la respuesta contiene en data un arreglo de objetos
   * Chequea que data exista y que sea un arreglo
   */
  static getApiRecursos(url: string, params?: QueryParams): Promise<JsonApiResponseRecursos> {
    return ApiClient.getApi<JsonApiResponseRecursos>(url, params)
      .then(resp => {
        if (!isArray(resp.data)) {
          return ApiClient.handleApiError(API_ERROR_INVALID_DATA); // reject, genera JsonApiError
        }
        return resp;
      });
  }

  /**
   * Request http POST genérico a la API al url indicado con data en body request.
   *  Retorna body del response (salvo que sea vacío).
   * El request es autenticado mediante sesión de usuario (cookies)
   * Por defecto el método retorna tipo JsonApiResponse, pero puede invocarse con tipo genérico
   */
  static async postApi(url: string, data: any | any[]): Promise<void | JsonApiResponse>; // Definición default (response vacío o JsonApiResponse)
  static async postApi<T>(url: string, data: any | any[]): Promise<T>; // Definición con tipo de retorno genérico
  static async postApi<T>(url: string, data: any | any[]): Promise<T> { // Implementación
    if (!url) {
      return ApiClient.sinUrlError('POST');
    }
    return this.axios.post<T>(url, { data: data }, { headers: await this.getHeadersRequestAutenticado() })
      .catch(error => ApiClient.handleAxiosError(error)) // Maneja error en el POST - hace reject con JsonApiError. Si es error de autenticación, desloguea.
      .then(resp => { // Si POST ok, retorna body response
        // console.log('POST API',resp);
        return resp.data;
      });
  }

  /**
   * Request http POST a la API con objeto ApiModel en request body.
   *  El método se encarga de formatearlo en JsonApiRecurso.
   *  Se asume que la respuesta retorna el recurso creado.
   */
  static postApiRecurso(url: string, objeto: ApiModel): Promise<JsonApiResponseRecurso> {
    // Transformo ApiModel en JsonApiRecurso
    let recurso = ApiClient.formatApiModelRecurso(objeto);
    // console.log('postApiRecurso '+url,objeto,recurso);
    return ApiClient.postApi<JsonApiResponseRecurso>(url, recurso);
  }

  /**
   * Request http POST a la API con arreglo de objetos ApiModel en request body.
   *  El método se encarga de formatearlos en arreglo de JsonApiRecurso.
   *  Se asume que la respuesta retorna un único recurso.
   */
  static postApiRecursos(url: string, objetos: ApiModel[]): Promise<JsonApiResponseRecursos> {
    // Transformo los ApiModel en JsonApiRecurso
    let recursos: JsonApiRecurso[] = objetos.map((objeto) => this.formatApiModelRecurso(objeto));
    // console.log('postApiRecursos '+url,objetos,recursos);
    return this.postApi<JsonApiResponseRecursos>(url, recursos);
  }

  /**
   * Request http PATCH genérico a la API al url indicado con data en body request.
   *  Retorna body del response.
   * El request es autenticado mediante sesión de usuario (cookies)
   * Por defecto el método retorna tipo JsonApiResponse, pero puede invocarse con tipo genérico
   */
  static async patchApi(url: string, data: any | any[]): Promise<void | JsonApiResponse>; // Definición default (response vacío o JsonApiResponse)
  static async patchApi<T>(url: string, data: any | any[]): Promise<T>; // Definición con tipo de retorno genérico
  static async patchApi<T>(url: string, data: any | any[]): Promise<T> { // Implementación
    if (!url) {
      return ApiClient.sinUrlError('PATCH');
    }
    return this.axios.patch<T>(url, { data: data }, { headers: await this.getHeadersRequestAutenticado() })
      .catch(error => ApiClient.handleAxiosError(error)) // Maneja error en el PATCH - hace reject con JsonApiError. Si es error de autenticación, desloguea.
      .then(resp => { // Si PATCH ok, retorna body response
        // console.log('PATCH API',resp);
        return resp.data;
      });
  }

  /**
   * Request http PATCH a la API con objeto ApiModel en request body.
   *  El método se encarga de formatearlo en JsonApiRecurso.
   *  Se asume que la respuesta retorna el recurso creado.
   *
   * @param url string URL endpoint
   * @param objeto ApiModel Objeto a convertir a Recurso y enviar en request
   * @param objeto_original_comparar Objeto original para comparar atributos modificados
   *                        Si se define, compara los atributos de objeto con objeto_original_comparar
   *                          e incluye en el request sólo los atributos modificados en objeto.
   */
  static patchApiRecurso(url: string, objeto: ApiModel, objeto_original_comparar?: ApiModel): Promise<JsonApiResponseRecurso> {
    // Transformo ApiModel en JsonApiRecurso
    let recurso = ApiClient.formatApiModelRecurso(objeto, objeto_original_comparar);
    // console.log('patchApiRecurso '+url,objeto,recurso);
    return ApiClient.patchApi<JsonApiResponseRecurso>(url, recurso);
  }

  /**
   * Request http PATCH a la API con arreglo de objetos ApiModel en request body.
   *  El método se encarga de formatearlos en arreglo de JsonApiRecurso.
   *  Se asume que la respuesta retorna la colección de recursos creado.
   */
  static patchApiRecursos(url: string, objetos: ApiModel[]): Promise<JsonApiResponseRecursos> {
    // Transformo los ApiModel en JsonApiRecurso
    let recursos: JsonApiRecurso[] = objetos.map((objeto) => this.formatApiModelRecurso(objeto));
    // console.log('patchApiRecursos '+url,objetos,recursos);
    return this.patchApi<JsonApiResponseRecursos>(url, recursos);
  }


  /**
   * Request http PUT a la API al url indicado con data parametrizada
   * El request es autenticado mediante sesión de usuario (cookies)
   * Por defecto el método no retorna nada (respuesta vacía), pero puede invocarse con tipo genérico
   */
  static async putApi(url: string, data?: any): Promise<void>; // Definición default (response vacío)
  static async putApi<T>(url: string, data?: any): Promise<T>; // Definición con tipo de retorno genérico
  static async putApi<T>(url: string, data: any = {}): Promise<T> { // Implementación
    if (!url) {
      return ApiClient.sinUrlError('PUT');
    }
    return this.axios.put<T>(url, { data: data }, { headers: await this.getHeadersRequestAutenticado() })
      .catch(error => ApiClient.handleAxiosError(error)) // Maneja error en el PUT - hace reject con JsonApiError. Si es error de autenticación, desloguea.
      .then(resp => { // Si PUT ok, retorna body response
        // console.log('PUT API',resp);
        return resp.data;
      });
  }

  /**
   * Request http PUT a la API con objeto ApiModel en request body.
   *  El método se encarga de formatearlos en arreglo de JsonApiRecurso.
   *  Se asume que la respuesta retorna la colección de recursos creado.
   */
  static putApiRecurso(url: string, objeto: ApiModel): Promise<JsonApiResponseRecurso> {
    // Transformo ApiModel en JsonApiRecurso
    let recurso = ApiClient.formatApiModelRecurso(objeto);
    return ApiClient.putApi<JsonApiResponseRecurso>(url, recurso);
  }

  /**
   * Request http DELETE genérico a la API al url indicado.
   * El request es autenticado mediante sesión de usuario (cookies)
   * Por defecto el método no retorna nada (respuesta vacía), pero puede invocarse con tipo genérico
   */
  static async deleteApi(url: string, data?: any): Promise<void>; // Definición default (response vacío)
  static async deleteApi<T>(url: string, data?: any): Promise<T>; // Definición con tipo de retorno genérico
  static async deleteApi<T>(url: string, data?: any): Promise<T> { // Implementación
    if (!url) {
      return ApiClient.sinUrlError('DELETE');
    }
    return this.axios.delete(url, { data: data, headers: await this.getHeadersRequestAutenticado() })
      .catch(error => ApiClient.handleAxiosError(error)) // Maneja error en el DELETE - hace reject con JsonApiError. Si es error de autenticación, desloguea.
      .then(resp => { // Si DELETE ok, retorna body response - ToDo chequear respuesta con content o vacía?
        // console.log('DELETE API',resp);
        return resp.data;
      });
  }

  /**
   * Request http DELETE incluyendo un ApiModel en request body.
   * El request es autenticado mediante sesión de usuario (cookies)
   * Por defecto el método no retorna nada (respuesta vacía), pero puede invocarse con tipo genérico
   */
  static deleteApiRecurso(url: string, objeto: ApiModel): Promise<JsonApiResponseRecurso> {
    let recurso = ApiClient.formatApiModelRecurso(objeto);
    return ApiClient.deleteApi<JsonApiResponseRecurso>(url, { data: recurso });
  }

  private static sinUrlError(method) {
    let msj = 'No se indica URL para realizar ' + method;
    console.error('Error request API: ' + msj);
    return Promise.reject({ detail: msj });
  }

  /**
   * Maneja error ocurrido por request a la API con el cliente Axios.
   * Procesa el error e invoca a handleApiError que lanza excepción
   */
  private static handleAxiosError(error: AxiosError): never {
    let error_data: any;
    if (error.response) {
      console.error('Error request API - response:', error.response);
      error_data = error.response.data.errors;
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      if (error.response.status === HTTP_STATUS_CODES.error_auth) {
        // Caso especial de error: usuario no autenticado.
        // Manejo general de error de autenticación (sesión inválida)
        // Realiza logout (redireccion a URL).
        return ApiClient.handleAuthError(error_data || API_ERROR_AUTH);
      }
    }
    else if (error.request) {
      // The request was made but no response was received
      // `error.request` is an instance of XMLHttpRequest
      console.error('Error request API:', error.request);
    }
    else {
      // Something happened in setting up the request that triggered an Error
      console.error('Error client API:', error.message);
    }
    if (!error_data) {
      error_data = API_ERROR_GENERICO;
    }
    return ApiClient.handleApiError(error_data);
  }

  /**
   * Maneja error de autenticación: fuerza redirección y neuvo chequeo (el pasaje a login efectivo se hace en router vue)
   * @param error
   */
  private static handleAuthError(error: JsonApiError[] | JsonApiError | string): never {
    console.error('Error autenticación');
    AUTH_SERVICE.desloguearse().then(() => {
      window.location.href = '/';
    });
    return this.handleApiError(error);
  }

  /**
   * Maneja error ocurrido por request a la API
   * Retorna JsonApiError (formato esperado de JSON-API - http://jsonapi.org/format/#errors)
   * Si la respuesta de la API tiene más de un error, retorna el primero
   */
  private static handleApiError(error: JsonApiError[] | JsonApiError | string): never {
    if (!error) {
      error = API_ERROR_GENERICO;
    }
    if (isArray(error)) {
      // Arreglo de JsonApiError: Obtiene el primer error del arreglo
      error = error[0];
    }
    console.error('ApiError', error);
    if (isString(error)) { // El error es string
      if ((<string> error).startsWith(PREFIX_API_ERROR)) {
        // Es código de error: obtengo detalle
        error = getApiErrorDetail(error);
      }
      // Genera objeto que cumple interface JsonApiError
      throw { detail: error };
    }
    // Si originalmente se había recibido un JsonApiError, directamente lo lanza
    throw error; // retorna promise rejected con JsonApiError
  }

  /**
   * Genera objeto JsonApiRecurso (formato esperado por la API al realizar POST)
   *  a partir de un objeto ApiModel con id, type, atributos y relaciones
   *
   * @param objeto ApiModel Objeto javascript a serializar (generar objeto JsonApiRecurso)
   * @param objeto_original_comparar Objeto original para comparar atributos modificados
   *                        Si se define, compara los atributos de objeto con objeto_original_comparar
   *                          e incluye en la respuesta sólo los atributos modificados en objeto.
   */
  private static formatApiModelRecurso(objeto: ApiModel, objeto_original_comparar?: ApiModel): JsonApiRecurso {
    let recurso: JsonApiRecurso = {
      type: objeto.api_type
    };
    if (objeto.id) {
      recurso.id = objeto.id;
    }

    /* Obtención de atributos a enviar en request
     *  Si se define objeto_original (sólo usado en PATCH), obtiene sólo los atributos modificados en objeto
     *    El método getAtributosModificados, obtiene los atributos correspondientes a comparar según el api_type
     * Si no, obtiene todos los atributos esperados en el request.
     *  A partir de v8.4.0, incluye los atributos con los keys definidos en el mapeo api_types_keys_atributos_request si se define,
     *    Para features anteriores, usa getter atributos_request
     */
    let attr = {};
    if (objeto_original_comparar) {
      attr = objeto.getAtributosModificados(objeto_original_comparar);
    }
    else {
      attr = objeto.getAtributosRequest();
    }

    if (!isEmpty(attr)) {
      recurso.attributes = attr;
    }
    let relaciones = objeto.getRelacionesRequest();
    if (!isEmpty(relaciones)) {
      recurso.relationships = {};
      for (let relacion in relaciones) {
        recurso.relationships[relacion] = {};
        let data_relacionada = relaciones[relacion];
        if (isArray(data_relacionada)) {
          recurso.relationships[relacion].data = [];
          for (let item of data_relacionada) {
            (<JsonApiRecurso[]>recurso.relationships[relacion].data).push(
              this.formatApiModelRecurso(item)
            );
          }
        }
        else {
          recurso.relationships[relacion] = {
            data: this.formatApiModelRecurso(data_relacionada)
          };
        }

      }
    }
    return recurso;
  }

  /**
   * Request http GET a la API al url indicado para realizar una descarga.
   * El request es autenticado mediante sesión de usuario (cookies)
   * Si la respuesta es correcta, hace un saveAs del archivo obtenido (en formato Blob)
   */
  static async descargaApi(url: string, params?: QueryParams): Promise<void> { // Implementación
    // console.log('descargaApi '+url,params);
    // ToDo header con Accept y el content-type esperado (no json-api)
    return this.axios.get(url, { headers: await this.getHeadersRequestAutenticado(), params: params, responseType: 'blob' })
    // GET retornando el body de la respuesta (archivo: Blob)
      .then(resp => {
        // Obtengo filename de header
        let filename = '';
        let header_content_disposition = resp.headers['content-disposition'];
        if (header_content_disposition) {
          // REgex para content disposition - https://regex101.com/r/hJ7tS6/1
          const regex = /filename[^;\n=]*=((['"]).*?\2|[^;\n]*)/g;
          const match = regex.exec(header_content_disposition);
          // El primer elemento del arreglo es full match, el segundo es el grupo que contiene el nombre de archivo
          if (match && match[1]) {
            filename = match[1].replace(/"/g, '');
          }
        }
        // Muestra a usuario filesaver
        saveAs(resp.data, filename);
      })
    // Maneja error en la descarga
      .catch((error: AxiosError) => {
        if (error.response) {
          // Se esperaba un blob, pero response es un JSON, por lo que se lee y parsea el JSON y luego hace reject con JsoApiError
          return ApiClient.handleApiErrorDescarga(error.response);
        }
        else {
          // No hay respuesta, realizo manejo genérico
          return ApiClient.handleAxiosError(error);
        }
      });
  }

  /**
   * Maneja error ocurrido por request de descarga a la API
   * Si ocurre error, la respuesta está en JSON, pero la respuesta se esperaba en Blob
   *     Es necesario convertir el error a JSON, usando FileReader
   * Luego invoca handleApiError
   */
  private static handleApiErrorDescarga(error_response: AxiosResponse): Promise<never> {
    console.error('Error request API descarga', error_response);
    return new Promise<any>((resolve) => {
      let reader = new FileReader();
      // Se asume que la respuesta está en formato JSON
      reader.onload = () => {
        try {
          const error_json = JSON.parse(<string>reader.result);
          // Crea nuevo error response y hace reject del promise usando la lógica de handleApiError
          resolve(error_json.errors);
        } catch (e) {
          console.error('Error descarga - Formato inválido', e);
          resolve(API_ERROR_DESCARGA);
        }
      };
      reader.onerror = (e) => {
        console.error('Error descarga - No se puede leer el mensaje de error', e);
        reader.abort();
        resolve(API_ERROR_DESCARGA);
      };
      // Lee el JSON de error. El listener anterior se encarga del parse
      reader.readAsText(error_response.data);
    })
      .then(error => ApiClient.handleApiError(error));
  }
}